import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Track from '../../../models/track';
import {
  EditableTrackField,
  editableTrackFields,
  isNumericTrackField,
  isUserEdited,
  mergeTrackFields,
  MIXED,
  TrackEdits,
  yearToReleaseDate,
} from '../../../utilities/trackEdits';
import Dialog from '../dialog/dialog';
import InfoBubble from '../infoBubble/infoBubble';
import s from './editTrackInfo.module.scss';

/**
 * The track editor (docs/TAG-EDITING.md, tier one).
 *
 * Edits the library record only; the audio file in the cloud is not touched.
 * With several tracks selected a field they disagree on shows as mixed and is
 * left alone unless typed into, so setting the album on ten tracks cannot
 * flatten their titles. Only fields the user actually changed are saved.
 *
 * Rendered into document.body so it sits above the virtualised lists, which
 * clip and transform their children.
 */

interface Props {
  tracks: Track[];
  onSave(edits: TrackEdits): void;
  onCancel(): void;
}

const labels: Record<EditableTrackField, string> = {
  title: 'Title',
  artist: 'Artist',
  album: 'Album',
  albumArtist: 'Album artist',
  genre: 'Genre',
  releaseDate: 'Year',
  trackNumber: 'Track #',
  disc: 'Disc',
  composer: 'Composer',
  comment: 'Comment',
  bpm: 'BPM',
  initialKey: 'Key',
  publisher: 'Publisher',
};

const MIXED_PLACEHOLDER = '<mixed>';

/** The fields that take a whole row of the form; the rest pair up. */
const wideFields: ReadonlySet<EditableTrackField> = new Set<EditableTrackField>([
  'title',
  'artist',
  'album',
]);

type Values = Record<EditableTrackField, string>;

/**
 * What a typed value becomes on save. `undefined` means "do not touch",
 * which covers both an untouched field and one that failed to parse - the
 * form blocks Save on the latter, so it never reaches here dirty.
 */
function parseEdit(
  field: EditableTrackField,
  raw: string,
): TrackEdits[EditableTrackField] | undefined {
  const text = raw.trim();

  if (text === '') {
    // Blank clears the field and lets a later tag read fill it. Title is the
    // exception: it cannot be blank, so blanking it is ignored.
    return field === 'title' ? undefined : null;
  }

  if (field === 'releaseDate') {
    return /^\d{4}$/.test(text) ? yearToReleaseDate(+text) : undefined;
  }

  if (isNumericTrackField(field)) {
    const n = parseInt(text, 10);

    return /^\d+$/.test(text) && n > 0 ? n : undefined;
  }

  return text;
}

function isValid(field: EditableTrackField, raw: string) {
  const text = raw.trim();

  if (text === '') return true;

  return parseEdit(field, raw) !== undefined;
}

export default function EditTrackInfo(props: Props) {
  const { tracks, onSave, onCancel } = props;

  const merged = useMemo(() => mergeTrackFields(tracks), [tracks]);

  const [values, setValues] = useState<Values>(() => {
    const initial = {} as Values;

    for (const field of editableTrackFields) {
      const value = merged[field];

      initial[field] = value === MIXED || value === undefined ? '' : String(value);
    }

    return initial;
  });
  const [dirty, setDirty] = useState<ReadonlySet<EditableTrackField>>(() => new Set());

  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(function focusFirstField() {
    firstInputRef.current?.focus();
    firstInputRef.current?.select();
  }, []);

  const multiple = tracks.length > 1;
  const fields = editableTrackFields.filter((x) => !(multiple && x === 'title'));

  const invalid = fields.filter((x) => dirty.has(x) && !isValid(x, values[x]));
  const canSave = dirty.size > 0 && invalid.length === 0;

  function handleChange(field: EditableTrackField, value: string) {
    setValues((prev) => ({ ...prev, [field]: value }));
    setDirty((prev) => {
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  }

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();

    if (!canSave) return;

    const edits: TrackEdits = {};

    for (const field of fields) {
      if (!dirty.has(field)) continue;

      const parsed = parseEdit(field, values[field]);

      if (parsed !== undefined) {
        (edits as any)[field] = parsed;
      }
    }

    onSave(edits);
  }

  /** "Edited by you" marker; counts so a mixed selection says how many. */
  function editedMarker(field: EditableTrackField) {
    const count = tracks.filter((x) => isUserEdited(x, field)).length;

    if (count === 0) return null;

    const title = multiple
      ? `Edited by you on ${count} of ${tracks.length} tracks`
      : 'Edited by you';

    return (
      <span className={s.edited} title={title}>
        ✎
      </span>
    );
  }

  return (
    <Dialog
      titleId="editTrackInfoTitle"
      onClose={onCancel}
      onSubmit={handleSubmit}
      title={
        <>
          {multiple ? `Edit info (${tracks.length} tracks)` : 'Edit info'}
          <InfoBubble label="What editing track info does">
            <p>
              <b>Edits change Fadila's library only.</b> The audio file in Google Drive, OneDrive or
              Dropbox is not modified, so other players and apps keep seeing the file's own tags.
            </p>
            <p>
              A field you set is marked ✎ and kept when you <b>Refetch Tags</b>. To hand it back to
              the file, use <b>Discard my edits and refetch</b> in the track's menu.
            </p>
            <p>
              With several tracks selected only the fields you type into change; a field shown as
              &lt;mixed&gt; stays as it is on each track.
            </p>
            <p>
              Blank a field to clear it and let the file's tag fill it again. Title cannot be blank.
            </p>
            <p>
              Saved with your library to your storage provider a few minutes after the edit, or
              straight away on an iPhone or iPad.
            </p>
          </InfoBubble>
        </>
      }
    >
      {!multiple && <p className={s.subheading}>{tracks[0].title}</p>}

      <div className={s.fields}>
        {fields.map((field, i) => {
          const mixed = merged[field] === MIXED && !dirty.has(field);
          const bad = invalid.includes(field);

          return (
            <label key={field} className={classNames(s.field, { [s.wide]: wideFields.has(field) })}>
              <span className={s.label}>
                {labels[field]}
                {editedMarker(field)}
              </span>
              <input
                ref={i === 0 ? firstInputRef : undefined}
                name={field}
                type="text"
                inputMode={isNumericTrackField(field) ? 'numeric' : undefined}
                className={classNames(s.input, { 'is-invalid-input': bad, [s.mixed]: mixed })}
                placeholder={mixed ? MIXED_PLACEHOLDER : undefined}
                value={values[field]}
                onChange={(ev) => handleChange(field, ev.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
          );
        })}
      </div>

      <p className={s.help}>
        {multiple ? 'Fields shown as <mixed> are left as they are unless you type into them. ' : ''}
        Blank a field to clear it; the next tag read may fill it again. Edits change the library
        only, not the file.
      </p>

      <div className={s.actions}>
        <button type="button" className="button secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="button" disabled={!canSave}>
          Save
        </button>
      </div>
    </Dialog>
  );
}
